/**
 * CastReady Module Registry
 *
 * Single source of truth for the 8 platform modules. All routing labels,
 * marketing landing paths, content groups, and analytics event names are
 * derived from this file. Add/remove modules here only.
 */

import type { SalesCallTopic } from "@/lib/salesCallConfig";

export type ModuleSlug =
  | "callback-zone"
  | "coach"
  | "castings"
  | "pro"
  | "crew"
  | "hmu"
  | "capture"
  | "draftline";

export interface ModuleDef {
  slug: ModuleSlug;
  name: string;
  shortName: string;
  description: string;
  /**
   * GTM tier per founding-pilot wedge (deck v16).
   * - `wedge`     — actively marketed now (Coach + Callback Zone).
   * - `expansion` — fully live but ranked secondary in marketing surfaces.
   * - `invite`    — invite-only partner portal (Pro). Excluded from both lists.
   */
  tier: "wedge" | "expansion" | "invite";
  /** Public marketing landing path (e.g. /platforms/coach). */
  marketingPath: string;
  /** In-app route prefix (e.g. /coach). */
  appPathPrefix: string;
  /** Dedicated demo route (e.g. /coach-demo). Omitted for invite-only modules. */
  demoPath?: string;
  /** GA4 content group name. Matches CONTENT_GROUPS in analyticsConfig. */
  contentGroup: string;
  /** Canonical view-landing event (back-compat). */
  viewLandingEvent: string;
  /** Optional GTM badge. */
  badge?: "Pilot" | "Invite";
  /** Display order — natural Actor Journey sequence in nav. */
  order: number;
  /**
   * Independent per-module signup pipeline.
   * When present, the landing-page CTA opens `/signup/<slug>/plan` and
   * walks the user through plan → account → card. Omit for invite-only
   * modules (e.g. Pro) — those keep Calendly as the entry point.
   */
  signup?: {
    /** STRIPE_PRODUCTS keys offered on the plan-picker step. */
    tiers: string[];
    /** Tier pre-selected on the plan picker. Must be in `tiers`. */
    defaultTier: string;
    /** Trial length in days. 0 = charge immediately. */
    trialDays: number;
  };
  /**
   * GTM CTA configuration for the public landing page.
   * - `self_serve`: primary CTA opens `/signup/<slug>/plan`.
   * - `book_call`: primary CTA opens the matching Calendly (`bookCallTopic`).
   *   When `secondarySelfServe` is true, a "Start small — self-serve" link
   *   is shown alongside it for smaller buyers (only valid when `signup`
   *   is also defined).
   */
  gtm?: {
    primary: "self_serve" | "book_call";
    bookCallTopic?: SalesCallTopic;
    secondarySelfServe?: boolean;
  };
}

export const MODULES: ReadonlyArray<ModuleDef> = [
  {
    slug: "coach",
    name: "CastReady Coach",
    shortName: "Coach",
    description: "Studios, coaches & agencies",
    tier: "wedge",
    marketingPath: "/platforms/coach",
    appPathPrefix: "/coach",
    demoPath: "/coach-demo",
    contentGroup: "platform_coach",
    viewLandingEvent: "view_landing_coach",
    order: 1,
    signup: {
      // Solo only self-serve. Studios/agencies route to /coach/apply via a
      // contact-sales card rendered in PlanStep when audience is studio/org.
      // White-Label-Solo retired from self-serve 2026-06-09 (not in SOT v3).
      tiers: ["coach_starter", "coach_pro"],
      defaultTier: "coach_pro",
      trialDays: 0,
    },
    gtm: {
      primary: "self_serve",
      bookCallTopic: "coach",
      secondarySelfServe: true,
    },
  },
  {
    slug: "callback-zone",
    name: "Callback Zone",
    shortName: "Callback Zone",
    description: "Actors & families",
    tier: "wedge",
    marketingPath: "/platforms/callback-zone",
    appPathPrefix: "/callback-zone",
    demoPath: "/callback-zone-demo",
    contentGroup: "platform_callback_zone",
    viewLandingEvent: "view_landing_callback_zone",
    order: 2,
    signup: {
      tiers: ["callback_zone_starter", "callback_zone_premium"],
      defaultTier: "callback_zone_premium",
      trialDays: 7,
    },
    gtm: { primary: "self_serve" },
  },
  {
    slug: "castings",
    name: "CastReady Castings",
    shortName: "Castings",
    description: "Casting professionals",
    tier: "wedge",
    marketingPath: "/platforms/castings",
    appPathPrefix: "/castings",
    demoPath: "/castings-demo",
    contentGroup: "platform_castings",
    viewLandingEvent: "view_landing_castings",
    badge: "Pilot",
    order: 3,
    signup: {
      tiers: [
        "castings_talent_premium",
        "castings_talent_annual",
        "castings_team_starter",
        "castings_team_pro",
        "castings_agency_pro",
      ],
      defaultTier: "castings_talent_premium",
      trialDays: 0,
    },
    gtm: {
      primary: "book_call",
      bookCallTopic: "castings_pro",
      secondarySelfServe: true,
    },
  },
  {
    slug: "pro",
    name: "CastReady Pro",
    shortName: "Pro",
    description: "Industry partners",
    tier: "invite",
    marketingPath: "/platforms/pro",
    appPathPrefix: "/pro",
    contentGroup: "platform_pro",
    viewLandingEvent: "view_landing_pro",
    badge: "Invite",
    order: 4,
    gtm: { primary: "book_call", bookCallTopic: "pro" },
  },
  {
    slug: "crew",
    name: "CastReady Crew",
    shortName: "Crew",
    description: "Crew marketplace",
    tier: "expansion",
    marketingPath: "/platforms/crew",
    appPathPrefix: "/crew",
    demoPath: "/crew-demo",
    contentGroup: "platform_crew",
    viewLandingEvent: "view_landing_crew",
    order: 5,
    signup: {
      tiers: ["crew_starter"],
      defaultTier: "crew_starter",
      trialDays: 0,
    },
    gtm: { primary: "self_serve" },
  },
  {
    slug: "hmu",
    name: "CastReady HMU",
    shortName: "HMU",
    description: "Hair, makeup & wardrobe",
    tier: "expansion",
    marketingPath: "/platforms/hmu",
    appPathPrefix: "/hmu",
    demoPath: "/hmu-demo",
    contentGroup: "platform_hmu",
    viewLandingEvent: "view_landing_hmu",
    order: 6,
    signup: {
      tiers: ["hmu_starter"],
      defaultTier: "hmu_starter",
      trialDays: 0,
    },
    gtm: { primary: "self_serve" },
  },
  {
    slug: "capture",
    name: "CastReady Capture",
    shortName: "Capture",
    description: "Photographers & videographers",
    tier: "expansion",
    marketingPath: "/platforms/capture",
    appPathPrefix: "/capture",
    demoPath: "/capture-demo",
    contentGroup: "platform_capture",
    viewLandingEvent: "view_landing_capture",
    order: 7,
    signup: {
      tiers: ["capture_starter"],
      defaultTier: "capture_starter",
      trialDays: 0,
    },
    gtm: { primary: "self_serve" },
  },
  {
    slug: "draftline",
    name: "CastReady Draftline",
    shortName: "Draftline",
    description: "Screenwriters",
    tier: "expansion",
    marketingPath: "/platforms/draftline",
    appPathPrefix: "/draftline",
    demoPath: "/draftline-demo",
    contentGroup: "platform_draftline",
    viewLandingEvent: "view_landing_draftline",
    order: 8,
    signup: {
      tiers: ["draftline_starter"],
      defaultTier: "draftline_starter",
      trialDays: 0,
    },
    gtm: { primary: "self_serve" },
  },
] as const;

export const MODULE_BY_SLUG: Readonly<Record<ModuleSlug, ModuleDef>> =
  Object.freeze(
    MODULES.reduce((acc, m) => {
      acc[m.slug] = m;
      return acc;
    }, {} as Record<ModuleSlug, ModuleDef>),
  );

/**
 * Resolve the module a given pathname belongs to, if any.
 * Matches both marketing landing (/platforms/<slug>) and in-app (/<slug>/...).
 */
export function getModuleFromPath(pathname: string): ModuleDef | null {
  if (!pathname) return null;
  // Marketing landing
  for (const m of MODULES) {
    if (pathname === m.marketingPath || pathname.startsWith(m.marketingPath + "/")) {
      return m;
    }
  }
  // App prefix
  for (const m of MODULES) {
    if (pathname === m.appPathPrefix || pathname.startsWith(m.appPathPrefix + "/")) {
      return m;
    }
  }
  return null;
}

/**
 * Whether the pathname is the marketing landing or the in-app surface.
 */
export function getModuleSurface(pathname: string): "marketing" | "app" | null {
  if (!pathname) return null;
  for (const m of MODULES) {
    if (pathname === m.marketingPath || pathname.startsWith(m.marketingPath + "/")) {
      return "marketing";
    }
  }
  for (const m of MODULES) {
    if (pathname === m.appPathPrefix || pathname.startsWith(m.appPathPrefix + "/")) {
      return "app";
    }
  }
  return null;
}

/**
 * Canonical SEO/meta tuple for a module landing page. Use this on every
 * `/platforms/<slug>` page so titles and descriptions cannot drift from
 * the registry. Pass the returned `title` and `description` straight into
 * `<PageMeta />`.
 */
export function getModuleMeta(slug: ModuleSlug): {
  name: string;
  shortName: string;
  description: string;
  title: string;
  demoPath?: string;
} {
  const m = MODULE_BY_SLUG[slug];
  return {
    name: m.name,
    shortName: m.shortName,
    description: m.description,
    title: `${m.name} — ${m.description}`,
    demoPath: m.demoPath,
  };
}

/** Path to the per-module signup pipeline plan-picker step. */
export function getModuleSignupPath(slug: ModuleSlug): string | null {
  const m = MODULE_BY_SLUG[slug];
  return m.signup ? `/signup/${slug}/plan` : null;
}

/** Path to the per-module sign-in screen. */
export function getModuleLoginPath(slug: ModuleSlug): string {
  return `/login/${slug}`;
}